import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import { track } from '@adsk/acs-web-analytics';
import { loadScripts } from '@adsk/alloy-helpers';
import { useUser } from '@adsk/alloy-provider';
import { SimpleModal } from '@adsk/alloy-react-modal';
import theme, { getInteractiveStyles } from '@adsk/alloy-react-theme';

// eslint-disable-next-line spellcheck/spell-checker
const MARKETING_SURVEY_GUIDE_ID = 'alyTR0tUIBfa76oinWvJ5Aq2dgM';
const SCRIPT_KEY = 'MARKETO_NEW_USER_SURVEY';
const SCRIPT = {
  src: '//constructioncloud.autodesk.com/js/forms2/js/forms2.min.js',
};
const ANALYTICS = {
  VIEW: 'onboarding.marketing_form.view',
  SUBMIT: 'onboarding.marketing_form.submit',
};

/**
 * Marketing survey for new users.
 */
const MarketingSurvey = React.forwardRef(({ setVisibility }, ref) => {
  const { user } = useUser();
  const { firstName, lastName, email, oxygenId, autodeskId } = user;
  // Load Marketo form only once.
  useEffect(() => {
    window.MktoForms2?.loadForm(
      '//constructioncloud.autodesk.com',
      '572-JSV-775',
      2354,
      (form) => {
        form.vals({
          FirstName: firstName,
          LastName: lastName,
          Email: email,
          oxygenID: autodeskId || oxygenId,
        });

        form.onSuccess(() => {
          window.pendo.updateOptions({
            visitor: { marketingSurveyComplete: true },
          });
          setVisibility(false);
          track(ANALYTICS.SUBMIT);

          return false;
        });
      }
    );
  }, [firstName, lastName, email, oxygenId, autodeskId, setVisibility]);

  return (
    <form
      ref={ref}
      id="mktoForm_2354"
      css={[
        {
          margin: '0 auto',
          maxWidth: 300,
          width: '100% !important',

          '.mktoFormRow h2': {
            ...theme.typography.heading2,
            marginBottom: 16,
            textAlign: 'center',
          },

          '.mktoHtmlText': {
            width: '100% !important',
          },

          '.mktoLabel': {
            ...theme.typography.labelMedium,
            marginTop: 10,
            width: 'auto !important',
          },

          'div.mktoGutter, div.mktoFormCol, div.mktoFieldWrap': {
            float: 'none',
          },

          '.mktoFieldWrap input.mktoField, .mktoFieldWrap select.mktoField': {
            background: 'none',
            border: `1px solid ${theme.colors.charcoal400}`,
            borderRadius: 3,
            color: theme.colors.charcoal900,
            height: 36,
            left: 0,
            marginTop: 4,
            outline: 'none',
            padding: '0 12px 1px', // Visually center text
            width: '100%',

            ...getInteractiveStyles(false),
          },

          '.mktoField': {
            float: 'none',
            marginTop: 4,
            width: '100% !important',
          },

          'div.mktoError': {
            color: theme.colors.charcoal200,
          },

          '.mktoError div.mktoErrorArrow': {
            background: theme.colors.charcoal200,
            border: `1px solid ${theme.colors.charcoal200}`,
          },

          '.mktoError div.mktoErrorMsg': {
            background: theme.colors.charcoal200,
            border: 'none',
            boxShadow: 'none',
            color: theme.colors.black,
            padding: '8px 12px',
            textShadow: 'none',
          },

          '.mktoButtonRow': {
            marginTop: 20,
          },

          '.mktoButtonWrap.mktoSimple button.mktoButton': {
            ...theme.typography.labelMedium,
            background: theme.colors.adskBlue500,
            border: '1px solid transparent',
            borderRadius: 2,
            color: theme.colors.white,
            height: 36,
            padding: '0 15px',

            ...getInteractiveStyles(false),

            '&:hover, &:focus': {
              borderColor: 'transparent',
            },

            '&:active': {
              background: theme.colors.adskBlue600,
              border: `1px solid ${theme.colors.charcoal500}`,
            },
          },
        },
      ]}
    ></form>
  );
});

MarketingSurvey.displayName = 'MarketingSurvey';

MarketingSurvey.propTypes = {
  /* Toggle modal visibility after survey is complete */
  setVisibility: PropTypes.func.isRequired,
};

/**
 * Modal that renders marketing survey.
 */
const MarketingSurveyModal = React.forwardRef((props, ref) => {
  const [visible, setVisibility] = useState(false);

  useEffect(() => {
    let cancel = false;
    let timeoutId;
    const checkPendoReady = () => {
      if (window.pendo?.guides) {
        if (window.pendo.findGuideById(MARKETING_SURVEY_GUIDE_ID)) {
          loadScripts(SCRIPT_KEY, [SCRIPT], () => {
            if (!cancel) {
              setVisibility(true);
              track(ANALYTICS.VIEW);
            }
          });
        }
      } else {
        if (!cancel) {
          timeoutId = setTimeout(checkPendoReady, 500);
        }
      }
    };
    checkPendoReady();
    return () => {
      cancel = true;
      timeoutId && clearTimeout(timeoutId);
    };
  }, [setVisibility]);

  return (
    <SimpleModal
      {...props}
      ref={ref}
      show={visible}
      disableModalExit
      style={{ paddingTop: 5, width: 400 }}
    >
      <MarketingSurvey setVisibility={setVisibility} />
    </SimpleModal>
  );
});

MarketingSurveyModal.displayName = 'MarketingSurveyModal';

export default MarketingSurveyModal;
